import { useChatStatus } from '@ai-sdk-tools/store';
import { NetworkStatus, useApolloClient, useMutation, useQuery, type ApolloCache } from '@apollo/client';
import { useFeatureFlag, useIsMobile } from '@newfront-insurance/admin-ui';
import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { CircleSpinnerInline, IconAdd2, SegmentedControlV2, useFuzzy } from '@newfront-insurance/core-ui';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButtonAsButton,
  IconSearch,
  NewfrontLogo,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';
import { useNotify } from '@newfront-insurance/next-notifications';
import { ChevronLeft, Clock, MoreVertical, Settings, X } from 'lucide-react';
import {
  createContext,
  Fragment,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IconOutlook, IconSidebar } from './icons';
import { WtwLogo } from './wtw-logo';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../lib/nova-schedule-proposal';
import type { ChatAppType, GetChatsByFilters } from '../../../queries/chats';
import { GET_CHAT_WITH_MESSAGES, GET_CHATS_BY_FILTERS, type GetChatsByFiltersChat } from '../../../queries/chats';
import { ARCHIVE_CHAT, RENAME_CHAT } from '../../../queries/mutations';
import type { ChatSettingsSection } from '../contexts/chat-overlay-context';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { novaGradientClass } from '../design-system';
import { useActiveSettingsSection } from '../hooks/use-active-settings-section';
import { useCanViewNovaAnalytics } from '../hooks/use-can-view-nova-analytics';
import {
  ACTIVE_CHAT_STATUS,
  ARCHIVED_CHAT_STATUS,
  buildOptimisticChat,
  hasMutationErrors,
} from '../lib/chat-optimistic-updates';
import { groupChatsByDate } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';
import { isWtwNovaHostname } from '../lib/wtw-branding';

const SIDEBAR_TRANSITION_DURATION = 200;

function isNovaAddin(appType?: ChatAppType) {
  return appType === 'OUTLOOK';
}

function useIsSidebarMobile() {
  const { isMobile } = useIsMobile();
  const { appType } = useChatOverlayContext();

  return isNovaAddin(appType) ? false : isMobile;
}

function UnreadDot({ className, showLabel = false }: { className?: string; showLabel?: boolean }) {
  return (
    <span
      className={cn('bg-wtw-coralreef-500 inline-block size-2 shrink-0 rounded-full', className)}
      data-testid="unread-indicator"
      role={showLabel ? 'status' : undefined}
    >
      {showLabel && <span className="sr-only">unread messages</span>}
    </span>
  );
}

const UNREAD_INDICATOR = <UnreadDot showLabel />;
function getCurrentHostname() {
  return typeof window === 'undefined' ? null : window.location.hostname;
}

function ChatSidebarBrandLogo() {
  if (isWtwNovaHostname(getCurrentHostname())) {
    return <WtwLogo />;
  }

  return (
    <span className="flex items-center justify-center">
      <NewfrontLogo />
    </span>
  );
}

interface ChatSidebarContextValue {
  isSidebarExpanded: boolean;
  /** True only after the width transition completes when expanding. Use for text/content rendering to prevent reflow. */
  isContentExpanded: boolean;
  chatHistoryFilter: 'current-account' | 'all-accounts';
  setChatHistoryFilter: (value: 'current-account' | 'all-accounts') => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchResults: GetChatsByFilters;
  isLoadingChats: boolean;
  isSearchActive: boolean;
  setIsSearchActive: (active: boolean) => void;
  currentAccountHasUnread: boolean;
  otherAccountsHaveUnread: boolean;
  hasCurrentChatInSidebar: boolean;
}

const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);

function useChatSidebarContext() {
  const context = useContext(ChatSidebarContext);
  if (!context) {
    throw new Error('ChatSidebar components must be used within ChatSidebar');
  }
  return context;
}

interface ChatSidebarProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Chat sidebar component with history, search, and filtering.
 *
 * On desktop, the sidebar is always visible in one of two states:
 * - **Collapsed** (63px): Shows only icon buttons (New Chat, Settings, Search)
 * - **Expanded** (288px): Shows full sidebar with labels, chat history, and filters
 *
 * On mobile, the sidebar preserves the previous overlay behavior:
 * hidden by default, shown as an overlay when toggled.
 *
 * The `isSidebarOpen` context value controls:
 * - Desktop: collapsed (false) vs expanded (true)
 * - Mobile: hidden (false) vs overlay (true)
 */
export function ChatSidebar({ className, children }: ChatSidebarProps) {
  const { isMobile: isMobileViewport } = useIsMobile();
  const {
    userUuid,
    accountUuid,
    setIsSidebarOpen,
    isSidebarOpen,
    isFullscreen,
    isStandaloneMode,
    isEmbedded,
    effectiveAppType,
    appType,
    hasNotifications,
    isSettingsOpen,
    setIsSettingsOpen,
    requestSettingsClose,
    hasActiveSettingsSubView,
    chatUuid,
  } = useChatOverlayContext();

  // In add-in mode (e.g. Outlook), the panel is narrow but should not
  // use mobile behavior — the sidebar should collapse with animation, not unmount.
  const isAddin = isNovaAddin(appType);
  const isMobile = isAddin ? false : isMobileViewport;

  const isSidebarExpanded = isSidebarOpen;

  // Delay expanded content rendering to avoid text reflow during the width transition.
  // When expanding: wait for the width transition to finish before showing text content.
  // When collapsing: immediately hide text content so it doesn't reflow as width shrinks.
  const [isContentExpanded, setIsContentExpanded] = useState(isSidebarExpanded);

  useEffect(() => {
    if (isSidebarExpanded) {
      const timer = setTimeout(() => setIsContentExpanded(true), SIDEBAR_TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
    setIsContentExpanded(false);
  }, [isSidebarExpanded]);

  const [chatHistoryFilter, setChatHistoryFilter] = useState<'current-account' | 'all-accounts'>(
    isEmpty(accountUuid) ? 'all-accounts' : 'current-account',
  );

  const [isSearchActive, setIsSearchActive] = useState(false);

  // Fetch chats for filtering and search
  const { data, networkStatus } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType: effectiveAppType,
      statusFilter: ACTIVE_CHAT_STATUS,
    },
    skip: isEmpty(userUuid),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};
  const isLoadingChats = networkStatus === NetworkStatus.loading && data === undefined;
  const hasCurrentChatInSidebar = useMemo(
    () => chats.some((chat) => normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid)),
    [chats, chatUuid],
  );

  // Filter chats by account
  const chatsFilteredByAccount = useMemo(() => {
    return chatHistoryFilter === 'current-account' && isNotEmpty(accountUuid)
      ? [...chats].filter((chat) => chat.accountUuid === accountUuid)
      : [...chats];
  }, [chats, accountUuid, chatHistoryFilter]);

  const { currentAccountHasUnread, otherAccountsHaveUnread } = useMemo(() => {
    let current = false;
    let other = false;
    for (const chat of chats) {
      if (!chat.hasUnreadMessages) continue;
      if (!isEmpty(accountUuid) && chat.accountUuid === accountUuid) current = true;
      else other = true;
      if (current && other) break;
    }
    return { currentAccountHasUnread: current, otherAccountsHaveUnread: other };
  }, [chats, accountUuid]);

  // Apply fuzzy search - useFuzzy manages its own query state
  const fuzzySearch = useFuzzy(chatsFilteredByAccount, {
    keys: ['title', 'summary'],
  });

  const contextValue: ChatSidebarContextValue = useMemo(
    () => ({
      isSidebarExpanded,
      isContentExpanded,
      chatHistoryFilter,
      setChatHistoryFilter,
      searchQuery: fuzzySearch.query,
      setSearchQuery: fuzzySearch.search,
      searchResults: fuzzySearch.results,
      isLoadingChats,
      isSearchActive,
      setIsSearchActive,
      currentAccountHasUnread,
      otherAccountsHaveUnread,
      hasCurrentChatInSidebar,
    }),
    [
      isSidebarExpanded,
      isContentExpanded,
      chatHistoryFilter,
      fuzzySearch.query,
      fuzzySearch.results,
      fuzzySearch.search,
      isLoadingChats,
      isSearchActive,
      currentAccountHasUnread,
      otherAccountsHaveUnread,
      hasCurrentChatInSidebar,
    ],
  );

  // Mobile: preserve current behavior (hidden when sidebar is closed)
  if (isMobile && !isSidebarOpen) {
    return null;
  }

  // Default composition for backward compatibility
  const defaultChildren = (
    <>
      <ChatSidebarNewChat />
      <ChatSidebarScheduledButton />
      <ChatSidebarSearch />
      <ChatSidebarFilterTabs />
      <ChatSidebarHistory />
    </>
  );

  const handleExitSettings = () => {
    if (hasActiveSettingsSubView) {
      requestSettingsClose();
      return;
    }

    setIsSettingsOpen(false);
  };

  return (
    <ChatSidebarContext.Provider value={contextValue}>
      {isSidebarOpen && (isMobile || isAddin) && (
        <div
          className="fixed inset-0 z-[1299] bg-[#000]/70 opacity-50 backdrop-blur-xs"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div
        className={cn('absolute -top-px h-[calc(100%+2px)]', {
          'right-full z-[-1]': !isFullscreen && !isEmbedded,
          'z-overlay left-0': (isFullscreen || isEmbedded) && !isMobile,
          'z-overlay right-0 left-0': isFullscreen && isMobile,
        })}
      >
        <div
          className={cn(
            'border-steel-75 relative flex h-full shrink-0 flex-col rounded-xs rounded-r-none border-y border-l',
            isSettingsOpen ? 'bg-[#FAFBFC]' : 'bg-steel-50 dark:bg-steel-500',
            'overflow-hidden transition-[width] duration-200',
            isSidebarExpanded ? 'w-[288px]' : isAddin ? 'w-0 border-none' : 'w-[63px]',
            !isFullscreen &&
              !isStandaloneMode &&
              !isEmbedded &&
              'shadow-floating-bar [clip-path:inset(-20px_0px_-20px_-20px)]',
            className,
          )}
        >
          {/* Sidebar header with toggle */}
          <header
            className={cn(
              'border-steel-75 flex h-14 shrink-0 items-center border-b px-[18px]',
              'text-steel-400',
              isContentExpanded ? 'justify-between' : 'justify-start',
            )}
          >
            {isContentExpanded && isSettingsOpen ? (
              <button
                type="button"
                onClick={handleExitSettings}
                className="hover:text-steel-500 animate-in fade-in flex min-w-0 items-center gap-[7px] px-[3px] text-sm font-semibold whitespace-nowrap duration-200"
              >
                <ChevronLeft className="size-[13px] shrink-0" />
                <span className="truncate">Back to Nova Assist</span>
              </button>
            ) : isContentExpanded ? (
              <h1 className="animate-in fade-in text-sm font-semibold whitespace-nowrap duration-200">Nova Assist</h1>
            ) : null}
            <div className="relative">
              <IconButtonAsButton
                aria-label={isSidebarOpen ? 'Collapse chat sidebar' : 'Expand chat sidebar'}
                data-testid="chat-sidebar-toggle-button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="hover:bg-steel-200/20 shrink-0"
              >
                <IconSidebar />
              </IconButtonAsButton>
              {!isContentExpanded && hasNotifications && <UnreadDot className="absolute top-0 right-0" />}
            </div>
          </header>

          {/* Sidebar body */}
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col gap-2 pt-5',
              isSettingsOpen && isContentExpanded ? 'px-5' : 'px-2',
            )}
          >
            {isSettingsOpen ? <ChatSidebarSettingsNavigation /> : (children ?? defaultChildren)}
          </div>

          {/* Standalone mode logo (only when expanded) */}
          {isStandaloneMode && isContentExpanded && !isSettingsOpen && (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-xs shadow-lg',
                'from-steel-50 via-steel-50/95 bg-gradient-to-t to-transparent',
                'dark:from-steel-500 dark:via-steel-500/95 dark:to-transparent',
              )}
            >
              <div className="flex h-full items-end justify-start px-[18px] pb-3">
                <ChatSidebarBrandLogo />
              </div>
            </div>
          )}
        </div>
      </div>
    </ChatSidebarContext.Provider>
  );
}

/**
 * New Chat button component for the sidebar.
 * Shows icon-only when collapsed, icon + text when expanded.
 */
export function ChatSidebarNewChat() {
  const {
    startNewChat,
    setIsSidebarOpen,
    setIsSettingsOpen,
    isSettingsOpen,
    settingsSection,
    isNewChat,
    hasActiveSettingsSubView,
    requestSettingsClose,
  } = useChatOverlayContext();
  const isMobile = useIsSidebarMobile();
  const { isContentExpanded, hasCurrentChatInSidebar } = useChatSidebarContext();

  const isPendingNewChat = isNewChat && !hasCurrentChatInSidebar;
  const isActive = isPendingNewChat && !isSettingsOpen && !hasActiveSettingsSubView && settingsSection !== 'scheduled';

  const openNewChat = () => {
    startNewChat();
    setIsSettingsOpen(false);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <MenuItem
      icon={<IconAdd2 color="var(--color-wtw-ultraviolet-850)" size={16} />}
      testId="nova-sidebar-new-chat"
      isActive={isActive}
      className="hover:text-wtw-stratosphere-825 font-semibold"
      iconClassName={cn(novaGradientClass.aiBackground, 'rounded-full')}
      onClick={() => {
        if (hasActiveSettingsSubView) {
          requestSettingsClose(openNewChat);
          return;
        }

        if (isPendingNewChat && !isSettingsOpen) {
          setIsSettingsOpen(false);
          if (isMobile) {
            setIsSidebarOpen(false);
          }
          return;
        }

        openNewChat();
      }}
    >
      {isContentExpanded && <span className="animate-in fade-in pl-1 whitespace-nowrap duration-200">New chat</span>}
    </MenuItem>
  );
}

/**
 * Built-in settings button for the sidebar.
 * Shows icon-only when collapsed, icon + text when expanded.
 * Toggles the settings panel via context.
 */
export function ChatSidebarSettingsButton() {
  const {
    isSettingsOpen,
    setIsSettingsOpen,
    setIsSidebarOpen,
    setSettingsSection,
    settingsSection,
    requestSettingsBack,
    hasActiveSettingsSubView,
  } = useChatOverlayContext();
  const isMobile = useIsSidebarMobile();
  const { isContentExpanded } = useChatSidebarContext();
  const isSettingsActive = isSettingsOpen || (hasActiveSettingsSubView && settingsSection !== 'scheduled');

  return (
    <MenuItem
      icon={<Settings className="size-6" />}
      testId="nova-sidebar-settings"
      isActive={isSettingsActive}
      allowActiveClick
      className="hover:text-steel-500 font-normal"
      onClick={() => {
        if (hasActiveSettingsSubView) {
          setIsSettingsOpen(true);
          requestSettingsBack();
        } else if (isSettingsOpen) {
          requestSettingsBack();
        } else {
          setSettingsSection('general');
          setIsSettingsOpen(true);
          if (!isMobile) {
            setIsSidebarOpen(true);
          }
        }
        if (isMobile) {
          setIsSidebarOpen(false);
        }
      }}
    >
      {isContentExpanded && <span className="animate-in fade-in whitespace-nowrap duration-200">Settings</span>}
    </MenuItem>
  );
}

export function ChatSidebarScheduledButton() {
  const {
    isSettingsOpen,
    setIsSidebarOpen,
    setSettingsSection,
    settingsSection,
    hasActiveSettingsSubView,
    requestSettingsBack,
  } = useChatOverlayContext();
  const { isContentExpanded } = useChatSidebarContext();
  const isMobile = useIsSidebarMobile();
  const areNovaSchedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const isScheduledActive = !isSettingsOpen && settingsSection === 'scheduled';

  if (!areNovaSchedulesEnabled) {
    return null;
  }

  return (
    <MenuItem
      icon={<Clock className="size-6" />}
      testId="nova-sidebar-scheduled"
      isActive={isScheduledActive}
      allowActiveClick
      className="hover:text-steel-500 font-normal"
      onClick={() => {
        if (isScheduledActive) {
          if (hasActiveSettingsSubView) {
            requestSettingsBack();
          } else {
            setSettingsSection('general');
          }
          return;
        }

        setSettingsSection('scheduled');
        setIsSidebarOpen(!isMobile);
      }}
    >
      {isContentExpanded && <span className="animate-in fade-in whitespace-nowrap duration-200">Scheduled</span>}
    </MenuItem>
  );
}

function ChatSidebarSettingsNavigation() {
  const { settingsSection, setSettingsSection, setIsSettingsOpen, hasActiveSettingsSubView, requestSettingsClose } =
    useChatOverlayContext();
  const { isContentExpanded } = useChatSidebarContext();
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);

  const canViewAnalytics = useCanViewNovaAnalytics();
  const activeSettingsSection = useActiveSettingsSection();
  const adminGroupId = useId();

  if (!isContentExpanded) {
    return null;
  }

  const handleSelectSection = (section: ChatSettingsSection) => {
    if (hasActiveSettingsSubView) {
      requestSettingsClose(() => {
        setSettingsSection(section);
        setIsSettingsOpen(true);
      });
      return;
    }

    if (settingsSection === section) {
      return;
    }

    setSettingsSection(section);
  };

  return (
    <nav className="flex flex-col gap-2 px-2" aria-label="Settings sections">
      <SettingsNavigationItem
        isActive={activeSettingsSection === 'general'}
        allowActiveClick={hasActiveSettingsSubView}
        onClick={() => handleSelectSection('general')}
      >
        General
      </SettingsNavigationItem>
      {isNovaSkillsEnabled ? (
        <SettingsNavigationItem
          isActive={activeSettingsSection === 'skills'}
          allowActiveClick={hasActiveSettingsSubView}
          onClick={() => handleSelectSection('skills')}
          testId="settings-skills-nav-item"
        >
          Skills
        </SettingsNavigationItem>
      ) : null}
      {canViewAnalytics ? (
        <div role="group" aria-labelledby={adminGroupId} className="flex flex-col gap-2">
          <span id={adminGroupId} className="text-steel-400 mt-4 px-[9px] text-xs font-semibold">
            Admin
          </span>
          <SettingsNavigationItem
            isActive={activeSettingsSection === 'analytics'}
            allowActiveClick={hasActiveSettingsSubView}
            onClick={() => handleSelectSection('analytics')}
            testId="settings-analytics-nav-item"
          >
            Skills &amp; Usage
          </SettingsNavigationItem>
        </div>
      ) : null}
    </nav>
  );
}

function SettingsNavigationItem({
  children,
  isActive,
  allowActiveClick,
  disabled,
  onClick,
  testId,
}: {
  children: React.ReactNode;
  isActive: boolean;
  allowActiveClick?: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      aria-current={isActive ? 'page' : undefined}
      disabled={disabled}
      onClick={(isActive && !allowActiveClick) || disabled ? undefined : onClick}
      data-testid={testId}
      className={cn(
        'flex h-8 w-full items-center justify-between rounded-[4px] px-[9px] py-1.5 text-left text-sm leading-5',
        isActive
          ? 'cursor-default bg-[#DEE2E7] font-semibold text-[#262D46]'
          : 'font-normal text-[#262D46] hover:bg-[#DEE2E7]/50 disabled:hover:bg-transparent',
      )}
    >
      <span className="truncate">{children}</span>
    </button>
  );
}

/**
 * Search component for the sidebar.
 * In collapsed mode: shows search icon that expands the sidebar when clicked.
 * In expanded mode: search is integrated into the history section titles (see ChatSidebarHistory).
 */
export function ChatSidebarSearch() {
  const { setIsSidebarOpen } = useChatOverlayContext();
  const { isContentExpanded, setIsSearchActive } = useChatSidebarContext();

  if (isContentExpanded) {
    return null;
  }

  return (
    <MenuItem
      icon={<IconSearch className="size-6" />}
      className="hover:text-steel-500"
      onClick={() => {
        setIsSidebarOpen(true);
        setIsSearchActive(true);
      }}
    />
  );
}

/**
 * Filter tabs component for the sidebar.
 * Hidden when sidebar is collapsed.
 */
export function ChatSidebarFilterTabs() {
  const { accountUuid } = useChatOverlayContext();
  const {
    isContentExpanded,
    chatHistoryFilter,
    setChatHistoryFilter,
    currentAccountHasUnread,
    otherAccountsHaveUnread,
  } = useChatSidebarContext();

  if (!isContentExpanded) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      <SegmentedControlV2
        options={[
          {
            label: 'Current account',
            value: 'current-account',
            disabled: isEmpty(accountUuid),
            indicator: currentAccountHasUnread ? UNREAD_INDICATOR : undefined,
          },
          {
            label: 'All accounts',
            value: 'all-accounts',
            disabled: isEmpty(accountUuid),
            indicator: otherAccountsHaveUnread ? UNREAD_INDICATOR : undefined,
          },
        ]}
        onChange={(value) => {
          setChatHistoryFilter(value as 'current-account' | 'all-accounts');
        }}
        value={isEmpty(accountUuid) ? 'all-accounts' : chatHistoryFilter}
        activeColor="default"
        containerClassname={cn('!border-border/50 !bg-background mx-2 !h-8 !rounded-full', {
          'opacity-50': isEmpty(accountUuid),
        })}
        optionClassname="!rounded-full !px-1.5 !border-transparent [&.isActive]:!bg-steel-100 !text-sm"
      />
    </div>
  );
}

/**
 * Chat history list component for the sidebar.
 * Hidden when sidebar is collapsed.
 * Includes inline search: a magnifying glass on the first date group header
 * that expands into a search input overlay when clicked.
 */
export function ChatSidebarHistory() {
  const isMobile = useIsSidebarMobile();
  const {
    chatUuid,
    setIsSidebarOpen,
    setActiveChat,
    startNewChat,
    setIsSettingsOpen,
    isSettingsOpen,
    settingsSection,
    isReadOnly,
    hasActiveSettingsSubView,
    requestSettingsClose,
    isStandaloneMode,
    userUuid,
    effectiveAppType,
  } = useChatOverlayContext();
  const notify = useNotify();
  const showAllNovaChats = effectiveAppType === null;
  const {
    isContentExpanded,
    searchQuery,
    setSearchQuery,
    searchResults,
    isLoadingChats,
    isSearchActive,
    setIsSearchActive,
  } = useChatSidebarContext();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [openMenuChatUuid, setOpenMenuChatUuid] = useState<string | null>(null);
  const [editingChatUuid, setEditingChatUuid] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [optimisticTitleByChatUuid, setOptimisticTitleByChatUuid] = useState<Record<string, string>>({});
  const [pendingActionChatUuids, setPendingActionChatUuids] = useState<string[]>([]);

  const activeListVariables = useMemo(
    () => ({
      userUuid,
      appType: effectiveAppType,
      statusFilter: ACTIVE_CHAT_STATUS,
    }),
    [effectiveAppType, userUuid],
  );

  const archivedListVariables = useMemo(
    () => ({
      userUuid,
      appType: effectiveAppType,
      statusFilter: ARCHIVED_CHAT_STATUS,
    }),
    [effectiveAppType, userUuid],
  );

  const updateChatTitleInCache = (cache: ApolloCache<unknown>, renamedChat: GetChatsByFiltersChat) => {
    if (!renamedChat.uuid) {
      return;
    }

    const updatedAt = renamedChat.updatedAt ?? new Date().toISOString();
    const activeData = cache.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: activeListVariables,
    });

    if (activeData?.chatsList) {
      cache.writeQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: activeListVariables,
        data: {
          chatsList: activeData.chatsList.map((chat) =>
            chat.uuid === renamedChat.uuid ? { ...chat, ...renamedChat, updatedAt } : chat,
          ),
        },
      });
    }

    for (const cacheUuid of new Set([renamedChat.uuid, normalizeChatUuid(renamedChat.uuid)])) {
      if (!cacheUuid) {
        continue;
      }

      const chatWithMessagesData = cache.readQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
      });

      if (chatWithMessagesData?.getChat) {
        cache.writeQuery({
          query: GET_CHAT_WITH_MESSAGES,
          variables: { uuid: cacheUuid },
          data: {
            getChat: {
              ...chatWithMessagesData.getChat,
              title: renamedChat.title,
              updatedAt,
            },
          },
        });
      }
    }
  };

  const removeChatFromActiveCache = (cache: ApolloCache<unknown>, chatToArchive: GetChatsByFiltersChat) => {
    const activeData = cache.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: activeListVariables,
    });

    if (activeData?.chatsList) {
      cache.writeQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: activeListVariables,
        data: {
          chatsList: activeData.chatsList.filter((chat) => chat.uuid !== chatToArchive.uuid),
        },
      });
    }
  };

  const addChatToArchivedCache = (cache: ApolloCache<unknown>, chatToArchive: GetChatsByFiltersChat) => {
    const archivedData = cache.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: archivedListVariables,
    });

    if (!archivedData?.chatsList || archivedData.chatsList.some((chat) => chat.uuid === chatToArchive.uuid)) {
      return;
    }

    cache.writeQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: archivedListVariables,
      data: {
        chatsList: [
          {
            ...chatToArchive,
            status: ARCHIVED_CHAT_STATUS,
            hasUnreadMessages: false,
            updatedAt: chatToArchive.updatedAt ?? new Date().toISOString(),
          },
          ...archivedData.chatsList,
        ],
      },
    });
  };

  const [renameChat] = useMutation(RENAME_CHAT, {
    update(cache, { data: mutationData }) {
      const renamedChat = mutationData?.renameChat;
      if (!renamedChat?.uuid) {
        return;
      }

      updateChatTitleInCache(cache, renamedChat);
    },
  });
  const client = useApolloClient();
  const [archiveChat] = useMutation(ARCHIVE_CHAT, {
    update(cache, { data: mutationData }) {
      const archivedChat = mutationData?.archiveChat;
      if (!archivedChat?.uuid) {
        return;
      }

      removeChatFromActiveCache(cache, archivedChat);
      addChatToArchivedCache(cache, archivedChat);
    },
  });

  // The active chat's in-progress state comes from the local stream status;
  // background chats rely on the server-reported activeStreamId.
  const chatStatus = useChatStatus();
  const isActiveChatStreaming = chatStatus === 'submitted' || chatStatus === 'streaming';

  const displayedSearchResults = useMemo(
    () =>
      searchResults.map((chat) =>
        chat.uuid && optimisticTitleByChatUuid[chat.uuid]
          ? { ...chat, title: optimisticTitleByChatUuid[chat.uuid] }
          : chat,
      ),
    [optimisticTitleByChatUuid, searchResults],
  );
  const groupedChats = useMemo(() => groupChatsByDate(displayedSearchResults), [displayedSearchResults]);
  const isSettingsActive = isSettingsOpen || hasActiveSettingsSubView;
  const isScheduleActive = !isSettingsOpen && settingsSection === 'scheduled';

  // Auto-focus the search input when opened (also re-trigger when content expands after sidebar transition)
  useEffect(() => {
    if (isSearchActive && isContentExpanded) {
      searchInputRef.current?.focus();
    }
  }, [isSearchActive, isContentExpanded]);

  if (!isContentExpanded) {
    return null;
  }

  const handleCloseSearch = () => {
    setSearchQuery('');
    setIsSearchActive(false);
  };

  const addPendingActionChatUuid = (pendingChatUuid: string) => {
    setPendingActionChatUuids((current) =>
      current.includes(pendingChatUuid) ? current : [...current, pendingChatUuid],
    );
  };

  const removePendingActionChatUuid = (pendingChatUuid: string) => {
    setPendingActionChatUuids((current) => current.filter((uuid) => uuid !== pendingChatUuid));
  };

  const removeOptimisticTitle = (chatUuidToRemove: string) => {
    setOptimisticTitleByChatUuid((current) => {
      if (!(chatUuidToRemove in current)) {
        return current;
      }

      const remainingTitles = { ...current };
      delete remainingTitles[chatUuidToRemove];
      return remainingTitles;
    });
  };

  async function handleRenameSubmit(chatToRename: GetChatsByFiltersChat, title: string) {
    const trimmedTitle = title.trim();
    setEditingChatUuid(null);
    setEditingTitle('');

    if (!chatToRename.uuid || !trimmedTitle || trimmedTitle === chatToRename.title) {
      return;
    }

    const optimisticRenamedChat = buildOptimisticChat(chatToRename, { title: trimmedTitle });
    setOptimisticTitleByChatUuid((current) => ({ ...current, [chatToRename.uuid!]: trimmedTitle }));
    updateChatTitleInCache(client.cache, optimisticRenamedChat);

    try {
      const result = await renameChat({
        variables: {
          chatUuid: chatToRename.uuid,
          title: trimmedTitle,
        },
        optimisticResponse: {
          renameChat: optimisticRenamedChat,
        },
      });
      if (hasMutationErrors(result) || !result.data?.renameChat) {
        throw new Error('Rename chat failed');
      }
    } catch {
      updateChatTitleInCache(client.cache, chatToRename);
      // Apollo removes the optimistic layer when the mutation fails.
    } finally {
      removeOptimisticTitle(chatToRename.uuid);
    }
  }

  async function handleArchive(chatToArchive: GetChatsByFiltersChat) {
    if (!chatToArchive.uuid) {
      return;
    }

    const isArchivingCurrentChat = normalizeChatUuid(chatToArchive.uuid) === normalizeChatUuid(chatUuid);
    if (chatToArchive.activeStreamId || (isArchivingCurrentChat && isActiveChatStreaming)) {
      notify.error('Please wait for Nova to finish responding before archiving this chat.');
      return;
    }

    addPendingActionChatUuid(chatToArchive.uuid);
    setOpenMenuChatUuid(null);
    if (isArchivingCurrentChat) {
      startNewChat();
      setIsSettingsOpen(false);
    }

    try {
      const result = await archiveChat({
        variables: {
          chatUuid: chatToArchive.uuid,
        },
        optimisticResponse: {
          archiveChat: buildOptimisticChat(chatToArchive, {
            status: ARCHIVED_CHAT_STATUS,
            hasUnreadMessages: false,
          }),
        },
      });
      if (hasMutationErrors(result) || !result.data?.archiveChat) {
        throw new Error('Archive chat failed');
      }
    } catch {
      if (isArchivingCurrentChat) {
        setActiveChat(chatToArchive.uuid, chatToArchive.accountUuid);
      }
    } finally {
      removePendingActionChatUuid(chatToArchive.uuid);
    }
  }

  return (
    <ScrollArea className={cn('-mx-2 mt-4 min-h-0 flex-1', isStandaloneMode && 'mb-20')}>
      <div className="flex flex-col gap-2 px-2 pb-5">
        {isLoadingChats && (
          <MenuItem icon={<CircleSpinnerInline />} className="text-foreground">
            Loading chats...
          </MenuItem>
        )}
        {/* First section title — always rendered so search has a stable anchor */}
        <SectionTitle>
          {isSearchActive ? (
            <div className="flex flex-1 items-center gap-1.5">
              <IconSearch className="text-steel-300 size-4 shrink-0" />
              <input
                ref={searchInputRef}
                className="text-foreground placeholder:text-steel-300 min-w-0 flex-1 bg-transparent text-sm font-normal tracking-normal outline-none"
                placeholder="Search chats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                type="button"
                onClick={handleCloseSearch}
                className="text-steel-300 hover:text-steel-400 shrink-0"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <>
              <span className="flex-1">{groupedChats[0]?.title ?? 'Today'}</span>
              <button
                type="button"
                onClick={() => setIsSearchActive(true)}
                className="text-steel-300 hover:text-steel-400 shrink-0"
              >
                <IconSearch className="size-4" />
              </button>
            </>
          )}
        </SectionTitle>
        {/* Chat groups — first group title already rendered above */}
        {groupedChats.map((group, groupIndex) => (
          <Fragment key={group.title}>
            {groupIndex > 0 && <SectionTitle>{group.title}</SectionTitle>}
            <div className="flex flex-col gap-2 px-0.5">
              {group.chats.map((chat) => {
                const isCurrentChat = normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid);
                const isActive = isCurrentChat && !isSettingsActive && !isScheduleActive;
                const selectChat = () => {
                  setActiveChat(chat.uuid!, chat.accountUuid);
                  setIsSettingsOpen(false);
                  if (isMobile) {
                    setIsSidebarOpen(false);
                  }
                };
                return (
                  <ChatMenuItem
                    key={chat.uuid}
                    allowActiveClick={isSettingsActive}
                    actionMenuOpen={openMenuChatUuid === chat.uuid}
                    disabledActions={isReadOnly}
                    editValue={editingTitle}
                    onClick={() => {
                      if (hasActiveSettingsSubView) {
                        requestSettingsClose(selectChat);
                        return;
                      }
                      selectChat();
                    }}
                    onArchive={() => void handleArchive(chat)}
                    onCancelRename={() => {
                      setEditingChatUuid(null);
                      setEditingTitle('');
                    }}
                    onChangeEditValue={setEditingTitle}
                    onCloseActionMenu={() => setOpenMenuChatUuid(null)}
                    onOpenActionMenu={() => setOpenMenuChatUuid(chat.uuid!)}
                    onRename={() => {
                      setOpenMenuChatUuid(null);
                      setEditingChatUuid(chat.uuid!);
                      setEditingTitle(chat.title);
                    }}
                    onSubmitRename={(title) => void handleRenameSubmit(chat, title)}
                    isActive={isActive}
                    // The active chat is on screen — unread state only applies to other chats.
                    hasNotifications={Boolean(chat.hasUnreadMessages) && !isCurrentChat}
                    isEditing={editingChatUuid === chat.uuid}
                    isInProgress={
                      pendingActionChatUuids.includes(chat.uuid ?? '') ||
                      (isCurrentChat
                        ? isActiveChatStreaming || chat.activeStreamId != null
                        : chat.activeStreamId != null)
                    }
                    isStandaloneMode={isStandaloneMode}
                    originAppType={showAllNovaChats && chat.appType === 'OUTLOOK' ? chat.appType : undefined}
                    testId="nova-sidebar-chat-row"
                    chatId={chat.uuid ?? undefined}
                  >
                    {chat.title}
                  </ChatMenuItem>
                );
              })}
            </div>
          </Fragment>
        ))}
        {isSearchActive && groupedChats.length === 0 && !isLoadingChats && (
          <p className="text-steel-400 px-6 text-sm">No results found</p>
        )}
      </div>
    </ScrollArea>
  );
}

/**
 * Settings wrapper component for the sidebar.
 * Provides a slot for apps to add their own settings components.
 * @deprecated Use ChatSidebarSettingsButton for the built-in settings toggle.
 */
export function ChatSidebarSettings({ children }: { children: ReactNode }) {
  const { isContentExpanded } = useChatSidebarContext();

  if (!isContentExpanded) {
    return null;
  }

  return <div className="flex shrink-0 flex-col gap-3">{children}</div>;
}

function AppOriginIcon({ appType }: { appType: ChatAppType }) {
  if (appType === 'OUTLOOK') {
    return <IconOutlook className="text-muted-foreground h-4 w-4" />;
  }

  return null;
}

function SectionTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex items-center gap-2 px-3 text-sm font-semibold tracking-widest',
        'bg-steel-50 text-steel-300 dark:bg-steel-500 dark:text-steel-300 uppercase',
        className,
      )}
    >
      {children}
    </div>
  );
}

function MenuItem({
  children,
  icon,
  className,
  iconClassName,
  iconStyle,
  onClick,
  disabled,
  isActive,
  allowActiveClick,
  testId,
}: {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  isActive?: boolean;
  allowActiveClick?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        'text-steel-400 relative flex w-full items-center gap-0 px-0 py-1.5 text-start',
        'dark:hover:bg-steel-400 rounded-[6px] hover:bg-transparent',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        {
          'bg-steel-75 text-steel-500 hover:bg-steel-75 cursor-default': isActive,
          'cursor-default hover:bg-transparent': !onClick,
        },
        className,
      )}
      onClick={isActive && !allowActiveClick ? undefined : onClick}
      disabled={disabled}
      aria-current={isActive ? 'page' : undefined}
      data-testid={testId}
    >
      {icon && (
        <div className="flex w-[47px] shrink-0 justify-center">
          <div className={cn('flex size-6 items-center justify-center', iconClassName)} style={iconStyle}>
            {icon}
          </div>
        </div>
      )}
      {children}
    </button>
  );
}

function ChatMenuItem({
  children,
  icon,
  className,
  iconClassName,
  iconStyle,
  onClick,
  actionMenuOpen,
  disabledActions,
  editValue,
  isEditing,
  onArchive,
  onCancelRename,
  onChangeEditValue,
  onCloseActionMenu,
  onOpenActionMenu,
  onRename,
  onSubmitRename,
  allowActiveClick,
  disabled,
  isActive,
  hasNotifications,
  isInProgress,
  isStandaloneMode,
  originAppType,
  testId,
  chatId,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  onClick?: () => void;
  actionMenuOpen?: boolean;
  disabledActions?: boolean;
  editValue?: string;
  isEditing?: boolean;
  onArchive?: () => void;
  onCancelRename?: () => void;
  onChangeEditValue?: (value: string) => void;
  onCloseActionMenu?: () => void;
  onOpenActionMenu?: () => void;
  onRename?: () => void;
  onSubmitRename?: (title: string) => void;
  allowActiveClick?: boolean;
  disabled?: boolean;
  isActive?: boolean;
  hasNotifications?: boolean;
  isInProgress?: boolean;
  isStandaloneMode?: boolean;
  originAppType?: ChatAppType;
  testId?: string;
  chatId?: string;
}) {
  const shouldSuppressClick = isActive && !allowActiveClick;
  const editInputRef = useRef<HTMLInputElement>(null);
  const title = typeof children === 'string' ? children : 'chat';
  const hasActions = Boolean(onRename || onArchive);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    editInputRef.current?.focus();
    editInputRef.current?.select();
  }, [isEditing]);

  if (isEditing) {
    return (
      <div
        className={cn(
          'text-steel-500 bg-steel-50 relative grid min-h-8 w-full grid-cols-[minmax(0,1fr)_32px] items-center rounded-[6px] px-2 py-1.5 text-start',
          hasActions && 'pr-8',
          className,
        )}
      >
        <div
          aria-hidden="true"
          className="invisible col-start-1 row-start-1 min-w-0 truncate whitespace-nowrap"
          data-testid="chat-rename-measure"
        >
          {children}
        </div>
        <input
          ref={editInputRef}
          className={cn(
            'col-start-1 row-start-1 h-5 w-full min-w-0 appearance-none self-center bg-transparent p-0',
            "font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#262D46]",
            'outline-none placeholder:text-[#BEC7D0]',
          )}
          data-testid="chat-rename-input"
          placeholder="Start typing..."
          value={editValue ?? ''}
          onBlur={(event) => {
            if (event.currentTarget.dataset.cancel === 'true') {
              return;
            }
            onSubmitRename?.(event.currentTarget.value);
          }}
          onChange={(event) => onChangeEditValue?.(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              event.nativeEvent.stopPropagation();
              event.currentTarget.dataset.cancel = 'true';
              onCancelRename?.();
              return;
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.dataset.cancel = 'true';
              onSubmitRename?.(event.currentTarget.value);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="group relative">
      <button
        type="button"
        aria-label={title}
        className={cn(
          'text-steel-500 relative grid w-full grid-cols-[minmax(0,1fr)_32px] items-center rounded-[6px] px-2 py-1.5 text-start',
          'dark:group-hover:bg-steel-400',
          'disabled:hover:bg-steel-50 disabled:cursor-not-allowed disabled:opacity-50',
          hasActions && 'pr-8',
          isActive ? 'bg-steel-75 group-hover:bg-steel-75 font-medium' : 'bg-steel-50 group-hover:bg-steel-100',
          {
            'cursor-default': shouldSuppressClick,
            'group-hover:bg-steel-50 cursor-default': !isActive && !onClick,
          },
          className,
        )}
        onClick={shouldSuppressClick ? undefined : onClick}
        disabled={disabled}
        aria-current={isActive ? 'page' : undefined}
        data-testid={testId}
        data-chat-id={chatId}
      >
        {icon && (
          <div className={cn('flex size-6 shrink-0 items-center justify-center', iconClassName)} style={iconStyle}>
            {icon}
          </div>
        )}
        <TooltipProvider delayDuration={350}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-w-0 truncate whitespace-nowrap">{children}</div>
            </TooltipTrigger>
            <TooltipContent>{children}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex min-w-8 items-center justify-end gap-1">
          {hasNotifications && <UnreadDot className="shrink-0" />}
          {originAppType && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex w-4 overflow-hidden opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  <AppOriginIcon appType={originAppType} />
                </span>
              </TooltipTrigger>
              <TooltipContent>Initiated on Outlook</TooltipContent>
            </Tooltip>
          )}
        </div>
      </button>
      {isInProgress && !hasNotifications && (
        <div
          className={cn(
            'pointer-events-none absolute top-1/2 right-1 flex size-6 -translate-y-1/2 items-center justify-center transition-opacity',
            'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0',
            actionMenuOpen && 'opacity-0',
          )}
        >
          <CircleSpinnerInline size={12} />
        </div>
      )}
      {hasActions && (
        <DropdownMenu
          open={actionMenuOpen}
          onOpenChange={(open) => {
            if (open) {
              onOpenActionMenu?.();
              return;
            }
            onCloseActionMenu?.();
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Chat actions for ${title}`}
              className={cn(
                'absolute top-1/2 right-1 flex size-6 -translate-y-1/2 items-center justify-center rounded-xs',
                'text-[#262D46] transition-opacity',
                disabledActions
                  ? 'cursor-not-allowed opacity-0 group-hover:opacity-50 focus-visible:opacity-50'
                  : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                actionMenuOpen && 'opacity-100',
              )}
              disabled={disabledActions}
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <MoreVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          {actionMenuOpen && (
            <DropdownMenuContent
              align="end"
              collisionPadding={{ bottom: isStandaloneMode ? 96 : 8, left: 8, right: 8, top: 8 }}
              sideOffset={4}
              className="z-overlay w-[102px] min-w-[102px] overflow-hidden rounded-[4px] border border-[#BEC7D0] bg-white p-0 shadow-[2px_2px_16px_rgba(38,45,70,0.15)]"
            >
              <DropdownMenuItem
                className="hover:bg-steel-50 focus:bg-steel-50 flex h-10 w-full cursor-pointer items-center rounded-none px-6 font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#222648]"
                onSelect={() => {
                  onRename?.();
                }}
              >
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                className="hover:bg-steel-50 focus:bg-steel-50 flex h-10 w-full cursor-pointer items-center rounded-none px-6 font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#222648]"
                onSelect={() => {
                  onArchive?.();
                }}
              >
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          )}
        </DropdownMenu>
      )}
    </div>
  );
}
